import { useState } from 'react';
import { MANIFESTS, WORKFLOWS, type ExampleFile } from '../content';
import { CodeBlock } from './CodeBlock';

/**
 * The full example files, verbatim.
 *
 * Shown complete rather than excerpted because the comments in them carry most
 * of the explanation — trimming them would remove the reason they are useful.
 */
export function Examples({ onLoadIntoValidator }: { onLoadIntoValidator: (yaml: string) => void }) {
  return (
    <>
      <section id="examples">
        <h2>Examples</h2>
        <p className="lede">
          Every file here is shipped in the action's repository and is reproduced in full — the
          comments are part of the documentation.
        </p>
      </section>

      <section id="examples-manifests" className="tight">
        <h3>Manifests</h3>
        <FileBrowser files={MANIFESTS} onLoadIntoValidator={onLoadIntoValidator} />
      </section>

      <section id="examples-workflows" className="tight">
        <h3>Workflows</h3>
        <p className="muted small">
          Copy one into your application repository as <code>.github/workflows/deploy.yml</code>.
        </p>
        <FileBrowser files={WORKFLOWS} />
      </section>
    </>
  );
}

function FileBrowser({
  files,
  onLoadIntoValidator,
}: {
  files: ExampleFile[];
  onLoadIntoValidator?: (yaml: string) => void;
}) {
  const [activeId, setActiveId] = useState(files[0]?.id);
  const active = files.find((f) => f.id === activeId) ?? files[0];

  if (!active) return null;

  return (
    <div className="browser">
      <ul className="filelist">
        {files.map((file) => (
          <li key={file.id}>
            <button
              type="button"
              aria-pressed={file.id === activeId}
              onClick={() => setActiveId(file.id)}
            >
              <span className="filename">{file.name}</span>
              {file.blurb && <span className="fileblurb">{file.blurb}</span>}
            </button>
          </li>
        ))}
      </ul>

      <div className="filebody">
        <div className="filebar">
          <code>{active.name}</code>
          <span className="grow" />
          <span className="muted small">{active.body.split('\n').length} lines</span>
          {onLoadIntoValidator && (
            <button
              type="button"
              className="linkish"
              onClick={() => {
                onLoadIntoValidator(active.body);
                document.getElementById('validator')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Open in validator ↑
            </button>
          )}
        </div>
        <CodeBlock code={active.body} numbered maxHeight={560} />
      </div>
    </div>
  );
}
